import * as Yup from "yup";
import i18next from "../../i18n";
import { AutocompleteFieldValue } from "../carbon/Form/AutocompleteField";

/**
 * Values `EditSiteForm` collects. `address` holds the `{address, googlePlaceId}` pair
 * `AutocompleteField` commits as ONE RHF value — same convention as `NewSiteFormValues`.
 * `country` is deliberately absent: the pre-Carbon `EditSiteForm` never rendered a country
 * control at all (see that file's history), and this rebuild doesn't add one — `onSubmit` keeps
 * sending the site's existing `country.id` unchanged, exactly as before.
 */
export type EditSiteFormValues = {
  internalCode: string;
  name: string;
  address: AutocompleteFieldValue;
  comment?: string;
  /** OC delivery config (Task 4.3). `leadTimeDays` is the raw input string ("" = not set);
   * `deliveryWindows` rows are `{value}` objects because `useFieldArray` can't hold bare strings.
   * Converted to/from the API shape by `hooks/useSiteDeliveryConfig`. */
  leadTimeDays?: string;
  deliveryWindows?: { value: string }[];
  deliveryRequirements?: { title: string; detail?: string }[];
  defaultRecipients?: { name?: string; email: string }[];
};

/**
 * Validation for the edit-site form (`PUT /site/{id}`), extracted out of `utils/validations.ts`
 * — deleted from there entirely (unlike `newSiteValidationSchema`, see that module's docstring):
 * grepping the codebase before this move found no importer of the old
 * `editSiteValidationSchema` besides `EditSiteForm.tsx` itself, which this task rewrites.
 *
 * `internalCode`/`name` are carried over verbatim (`.required()`, same messages). `address` is
 * new — the same single `.test()` `newSiteValidationSchema` uses, checking only that `address`
 * (not `googlePlaceId`) is non-empty. That distinction matters MORE here than on the create form:
 * `EditSiteForm`'s `defaultValues` seed this field from the record being edited
 * (`{address: site.address, googlePlaceId: site.googlePlaceId ?? ""}`), and a site provisioned
 * through the API (this flow's own e2e fixture, `provisionSite()`) legitimately has
 * `googlePlaceId: null` — never having gone through Google's widget at all. Requiring
 * `googlePlaceId` here would make such a site's address permanently un-savable without forcing a
 * re-selection through the Places widget on every edit, regardless of whether the user touched
 * the address — precisely the defect this task's brief calls out as the reason `EditSiteForm`
 * used to block every submit on a fresh Places selection, even for a site whose address was
 * already correct. `comment` stays unvalidated, same as `newSiteValidationSchema` — genuinely
 * optional, never `.required()` here or on the pre-Carbon form.
 */
export const editSiteValidationSchema = Yup.object().shape({
  internalCode: Yup.string().required(i18next.t("validations.internalCode")),
  name: Yup.string().required(i18next.t("validations.siteName")),
  address: Yup.object({
    address: Yup.string(),
    googlePlaceId: Yup.string(),
  }).test(
    "address-required",
    i18next.t("sites.errorPlace"),
    (value?: Partial<AutocompleteFieldValue>) => Boolean(value?.address)
  ),
  comment: Yup.string(),
  // Delivery config is entirely optional: an empty section validates. Only a row the user chose
  // to add is validated (a stray blank row is removable; it must not silently pass as data).
  leadTimeDays: Yup.string().test(
    "lead-time-int",
    i18next.t("sites.deliveryConfig.invalidLeadTime"),
    (value?: string) => !value || /^\d+$/.test(value.trim())
  ),
  deliveryWindows: Yup.array().of(
    Yup.object({
      value: Yup.string()
        .trim()
        .required(i18next.t("sites.deliveryConfig.windowRequired")),
    })
  ),
  deliveryRequirements: Yup.array().of(
    Yup.object({
      title: Yup.string()
        .trim()
        .required(i18next.t("sites.deliveryConfig.titleRequired")),
      detail: Yup.string(),
    })
  ),
  defaultRecipients: Yup.array().of(
    Yup.object({
      name: Yup.string(),
      email: Yup.string()
        .trim()
        .email(i18next.t("validations.validEmail"))
        .required(i18next.t("sites.deliveryConfig.emailRequired")),
    })
  ),
});
